import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import api from "@/lib/api";
import { CheckCircle2, XCircle, ShieldCheck, Award, Users, User as UserIcon } from "lucide-react";

/**
 * Public landing page hit by the QR code on every membership card. Now
 * tier-aware: the page chrome is tinted Bronze / Silver / Gold so the
 * post-scan experience matches the physical card the visitor just held up.
 */

// Tier palette mirrors the one in services/membership_card.py. Drives the
// top accent bar, tier ribbon pill, hero ring, and primary border tint.
// Detection mirrors backend `_tier_palette`:
//   1. plan_name contains "bronze"/"silver"/"gold" → use that.
//   2. else plan_tier maps composition → individual=Bronze, husband_wife/couple=Silver, family=Gold.
//   3. label = plan_name if it already carries the tier word, else "<Tier> Tier".
function tierTokens(planName, planTier) {
  const raw = (planName || "").trim();
  const n = raw.toLowerCase();
  const tn = (planTier || "").trim().toLowerCase();
  const pretty = raw.replace(/\w\S*/g, (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());

  let explicit = null;
  if (n.includes("bronze")) explicit = "bronze";
  else if (n.includes("silver")) explicit = "silver";
  else if (n.includes("gold") || n.includes("platinum")) explicit = "gold";
  if (!explicit) {
    if (tn === "individual") explicit = "bronze";
    else if (tn === "husband_wife" || tn === "husband-wife" || tn === "couple") explicit = "silver";
    else if (tn === "family") explicit = "gold";
  }
  const carriesTier = ["bronze", "silver", "gold", "platinum"].some((t) => n.includes(t));
  const label = carriesTier
    ? (pretty ? `${pretty} Tier` : "Tier")
    : (explicit ? `${explicit.charAt(0).toUpperCase()}${explicit.slice(1)} Tier` : (pretty ? `${pretty} Tier` : "Tier"));

  if (explicit === "bronze") {
    // Brick-bronze palette anchored on #933325 — matches the printed card.
    return {
      key: "bronze",
      label,
      stripe: "linear-gradient(90deg,#933325 0%,#6B2419 100%)",
      ribbonBg: "linear-gradient(135deg,#933325 0%,#6B2419 100%)",
      ribbonFg: "#FFFFFF",
      ringFrom: "#FBEAE6",
      ringTo: "#F2D2CB",
      borderTint: "rgba(147,51,37,0.45)",
      accent: "#933325",
    };
  }
  if (explicit === "silver") {
    return {
      key: "silver",
      label,
      stripe: "linear-gradient(90deg,#D4D7DD 0%,#6F7B8E 100%)",
      ribbonBg: "linear-gradient(135deg,#E0E4EA 0%,#8B97A8 100%)",
      ribbonFg: "#0F2540",
      ringFrom: "#F2F4F8",
      ringTo: "#DDE3EC",
      borderTint: "rgba(111,123,142,0.42)",
      accent: "#5F6B7E",
    };
  }
  return {
    key: "gold",
    label: label || "Gold Tier",
    // Pure 3-stop warm-gold sweep — matches the printed card (no maroon).
    stripe: "linear-gradient(90deg,#E6BE6A 0%,#C89B3C 50%,#A87A28 100%)",
    ribbonBg: "linear-gradient(135deg,#E6BE6A 0%,#A87A28 100%)",
    ribbonFg: "#0F2540",
    ringFrom: "#FBF1D7",
    ringTo: "#F0DBA0",
    borderTint: "rgba(200,155,60,0.48)",
    accent: "#A87A28",
  };
}

export default function VerifyMember() {
  const { memberId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/verify/${memberId}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Member not found"));
  }, [memberId]);

  const ok = data && data.status === "active";
  const tier = useMemo(() => tierTokens(data?.plan_name, data?.plan_tier), [data?.plan_name, data?.plan_tier]);

  return (
    <SiteLayout>
      <section className="mx-auto max-w-xl px-6 py-12 sm:py-16" data-testid="verify-page">
        <span className="overline">Membership verification</span>
        <h1 className="mt-2 font-display text-4xl font-bold text-[#0F2540]">QR Verification</h1>

        {err && (
          <div className="mt-8 rounded-3xl bg-white border border-[#E63946]/30 p-8 text-center" data-testid="verify-error">
            <XCircle className="h-10 w-10 mx-auto text-[#E63946]" />
            <p className="mt-3 font-semibold text-[#E63946]">{err}</p>
            <p className="mt-1 text-sm text-[#5C6B7A]">If you scanned a genuine NPC card, please contact Nirmal Hospitals.</p>
          </div>
        )}

        {data && (
          <div
            className="mt-8 rounded-3xl bg-white overflow-hidden shadow-sm"
            style={{ border: `1px solid ${tier.borderTint}` }}
            data-testid="verify-card"
          >
            {/* Tier-coloured accent stripe at the top, mirroring the card. */}
            <div className="h-1.5 w-full" style={{ background: tier.stripe }} />

            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${ok ? "bg-[#1F7A3D]/10 text-[#1F7A3D]" : "bg-[#E63946]/10 text-[#E63946]"}`}
                  data-testid="verify-status"
                >
                  {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {ok ? "Active membership" : `Status: ${data.status}`}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Shared-membership badge — visible at-a-glance cue that
                      the scan covers a family / husband-wife plan. */}
                  {data.is_shared && (
                    <div
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-[0.18em] font-bold uppercase"
                      style={{ background: "#1E4F87", color: "#FFFFFF" }}
                      title={`Covers ${data.total_members || data.members?.length || 1} members`}
                      data-testid="verify-shared-badge"
                    >
                      <Users className="h-3 w-3" />
                      Shared · {data.total_members || data.members?.length || 1}
                    </div>
                  )}
                  {/* Tier ribbon pill — matches the card's ribbon. */}
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.22em] font-extrabold uppercase shadow-sm"
                    style={{ background: tier.ribbonBg, color: tier.ribbonFg }}
                    data-testid="verify-tier-ribbon"
                  >
                    <Award className="h-3 w-3" />
                    {tier.label}
                  </div>
                </div>
              </div>

              {/* Hero block: name + membership number, anchored in a tier-tinted
                  rounded panel so the identity reads at a glance after scan. */}
              <div
                className="mt-6 rounded-2xl p-5"
                style={{
                  background: `linear-gradient(135deg, ${tier.ringFrom} 0%, ${tier.ringTo} 100%)`,
                }}
                data-testid="verify-hero"
              >
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70" style={{ color: tier.accent }}>
                  Member
                </div>
                <div className="mt-1 font-display text-2xl sm:text-3xl font-bold text-[#0F2540] leading-tight">
                  {data.full_name}
                </div>
                <div className="mt-1 text-sm font-bold tracking-wider" style={{ color: tier.accent }}>
                  {data.membership_number || data.member_id}
                </div>
              </div>

              <div className="mt-6 space-y-3 text-[#0F2540]">
                <Row label="Plan" value={data.plan_name} />
                <Row label="Valid Till" value={new Date(data.valid_till).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} />
                <Row label="Members on Plan" value={data.total_members || data.family_members_allowed} testid="verify-total-members" />
                {/* Hospital UHID — surfaced for reception staff at QR scan
                    time so they can pull the patient record without flipping
                    to admin. Only renders when staff has linked one. */}
                {data.uhid && (
                  <Row label="Hospital UHID" value={data.uhid} testid="verify-uhid" />
                )}
                {data.matched_member && (
                  <Row label="Scanned For" value={`${data.matched_member.full_name} · ${data.matched_member.relation || "Member"}`} />
                )}
                {data.matched_member?.uhid && data.matched_member.uhid !== data.uhid && (
                  <Row label="Scanned Member UHID" value={data.matched_member.uhid} testid="verify-matched-uhid" />
                )}
              </div>

              {/* Linked Members panel — shows every individual covered by
                  this shared membership so reception staff can validate the
                  entire household in a single scan. The card that triggered
                  the scan (primary or sub) is highlighted with a tier-tinted
                  background + "Scanned" pill. */}
              {data.is_shared && data.members && data.members.length > 1 && (
                <div className="mt-6 rounded-2xl overflow-hidden" style={{ border: `1px solid ${tier.borderTint}` }} data-testid="verify-linked-members">
                  <div className="flex items-center justify-between gap-3 px-4 py-3" style={{ background: `linear-gradient(135deg, ${tier.ringFrom} 0%, ${tier.ringTo} 100%)` }}>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" style={{ color: tier.accent }} />
                      <span className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: tier.accent }}>
                        Shared Membership · {data.members.length} members
                      </span>
                    </div>
                  </div>
                  <ul className="divide-y" style={{ borderColor: tier.borderTint }} data-testid="verify-linked-members-list">
                    {data.members.map((mb) => (
                      <li
                        key={mb.member_id}
                        className={`flex items-center justify-between gap-3 px-4 py-3 ${mb.is_scanned ? "bg-[#F4F7FB]" : "bg-white"}`}
                        data-testid={`verify-linked-row-${mb.member_id}`}
                      >
                        <div className="min-w-0 flex items-center gap-3">
                          <span
                            className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                            style={{ background: tier.ringTo, color: tier.accent }}
                            aria-hidden
                          >
                            <UserIcon className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-[#0F2540] truncate">{mb.full_name || "—"}</span>
                              {mb.is_primary && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1E4F87] text-white font-bold uppercase tracking-wider">Primary</span>
                              )}
                              {mb.is_scanned && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider"
                                  style={{ background: tier.ribbonBg, color: tier.ribbonFg }}
                                >
                                  Scanned
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-[#5C6B7A] mt-0.5">
                              <span className="font-mono">{mb.member_id}</span>
                              {mb.relation && (
                                <span className="capitalize"> · {mb.relation}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div
                className="mt-8 flex items-center gap-2 text-sm pt-4"
                style={{ borderTop: `1px solid ${tier.borderTint}`, color: tier.accent }}
                data-testid="verify-footer"
              >
                <ShieldCheck className="h-4 w-4" />
                Verified by Nirmal Hospitals
              </div>
            </div>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-[#E1E8F0] last:border-none" data-testid={testid}>
      <span className="text-sm text-[#5C6B7A]">{label}</span>
      <span className="font-semibold text-[#0F2540]">{value}</span>
    </div>
  );
}
